import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { GraduationCap, Plus, Trash2, Clock, Users, BookOpen, X, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { ce } from '../../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../../lib/dates';

const CATS = ['Laws & Regs', 'Safety', 'IPM', 'WDO', 'Urban', 'Vertebrate', 'Public Health', 'General'];

function AddCourseModal({ onClose, onSaved }) {
  const [f, setF] = useState({ title: '', description: '', category: 'General', ceHours: 1, type: 'video', url: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try { await ce.addCourse({ ...f, ceHours: Number(f.ceHours) }); toast.success('Course added'); onSaved(); onClose(); }
    catch { toast.error('Could not add course'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="add-course-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Add CE Course</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div><Label className="text-xs">Title</Label><Input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} className="mt-1 h-10" data-testid="course-title-input" /></div>
          <div><Label className="text-xs">Description</Label><textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} rows={2} className="mt-1 w-full text-sm border border-slate-200 rounded-md p-2" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">CE Hours</Label><Input type="number" step="0.5" min="0" value={f.ceHours} onChange={e => setF({ ...f, ceHours: e.target.value })} className="mt-1 h-10" data-testid="course-hours-input" /></div>
            <div><Label className="text-xs">Category</Label><Select value={f.category} onValueChange={v => setF({ ...f, category: v })}><SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger><SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Type</Label><Select value={f.type} onValueChange={v => setF({ ...f, type: v })}><SelectTrigger className="mt-1 h-10"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="video">Video</SelectItem><SelectItem value="document">Document</SelectItem><SelectItem value="quiz">Quiz</SelectItem></SelectContent></Select></div>
          </div>
          <div><Label className="text-xs">Content URL (optional)</Label><Input value={f.url} onChange={e => setF({ ...f, url: e.target.value })} placeholder="https://…" className="mt-1 h-10" /></div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="save-course-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Course'}</Button>
        </div>
      </div>
    </div>
  );
}

function ComplianceRow({ row, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState({ requiredHours: row.requiredHours, licenseNumber: row.licenseNumber, expiryDate: row.expiryDate });
  const save = async () => {
    try { await ce.setProfile({ email: row.email, requiredHours: Number(v.requiredHours), licenseNumber: v.licenseNumber, expiryDate: v.expiryDate }); toast.success('Updated'); setEdit(false); onSaved(); }
    catch { toast.error('Could not update'); }
  };
  return (
    <tr className="hover:bg-slate-50" data-testid={`compliance-row-${row.email}`}>
      <td className="px-4 py-3">
        <div className="font-semibold text-slate-900 text-sm">{row.name}</div>
        <div className="text-xs text-slate-500">{row.licenseNumber ? `#${row.licenseNumber}` : 'No license on file'}</div>
      </td>
      <td className="px-4 py-3 w-56">
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden"><div className={`h-full ${row.compliant ? 'bg-emerald-500' : 'bg-[#8BC53F]'}`} style={{ width: `${row.percent}%` }} /></div>
          <span className="text-xs font-bold tabular-nums text-slate-700">{row.earnedHours}/{row.requiredHours}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-slate-600">{row.expiryDate ? formatDate(row.expiryDate, { year: 'numeric', month: 'numeric', day: 'numeric' }) : '—'}</td>
      <td className="px-4 py-3">
        {row.compliant
          ? <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1"><CheckCircle2 className="w-3 h-3" />Compliant</Badge>
          : <Badge className="bg-amber-100 text-amber-700 border-0 gap-1"><AlertTriangle className="w-3 h-3" />{row.remaining} hrs short</Badge>}
      </td>
      <td className="px-4 py-3 text-right">
        {edit ? (
          <div className="flex items-center gap-1.5 justify-end flex-wrap">
            <Input type="number" value={v.requiredHours} onChange={e => setV({ ...v, requiredHours: e.target.value })} className="h-8 w-16" title="Required hours" />
            <Input type="date" value={v.expiryDate ? v.expiryDate.slice(0, 10) : ''} onChange={e => setV({ ...v, expiryDate: e.target.value })} className="h-8 w-36" />
            <Button size="sm" className="h-8 bg-[#8BC53F] hover:bg-[#6FA82C]" onClick={save} data-testid={`save-compliance-${row.email}`}>Save</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" className="h-8" onClick={() => setEdit(true)} data-testid={`edit-compliance-${row.email}`}>Edit</Button>
        )}
      </td>
    </tr>
  );
}

export default function EducationPage() {
  const [tab, setTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [c, comp] = await Promise.all([ce.courses(), ce.compliance().catch(() => [])]);
      setCourses(c); setCompliance(comp);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { try { await ce.deleteCourse(id); toast.success('Course removed'); load(); } catch { toast.error('Could not remove'); } };

  const totalHours = courses.reduce((s, c) => s + (c.ceHours || 0), 0);
  const compliantCount = compliance.filter(c => c.compliant).length;

  return (
    <div className="p-6 lg:p-8 space-y-5">
      <div className="flex flex-wrap justify-between gap-3 items-center">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2"><GraduationCap className="w-7 h-7 text-[#8BC53F]" />Continuing Education</h1>
          <p className="text-slate-500 mt-1">Keep your technicians' licenses current with tracked CE courses.</p>
        </div>
        {tab === 'courses' && <Button onClick={() => setShowAdd(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="add-course-btn"><Plus className="w-4 h-4" />Add Course</Button>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="p-4 border-slate-200 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-[#8BC53F]/15 text-[#5b8523] flex items-center justify-center"><BookOpen className="w-5 h-5" /></div><div><div className="text-2xl font-extrabold text-slate-900">{courses.length}</div><div className="text-xs text-slate-500">Courses</div></div></Card>
        <Card className="p-4 border-slate-200 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center"><Clock className="w-5 h-5" /></div><div><div className="text-2xl font-extrabold text-slate-900">{totalHours}</div><div className="text-xs text-slate-500">CE hours offered</div></div></Card>
        <Card className="p-4 border-slate-200 flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><Users className="w-5 h-5" /></div><div><div className="text-2xl font-extrabold text-slate-900">{compliantCount}/{compliance.length}</div><div className="text-xs text-slate-500">Techs compliant</div></div></Card>
      </div>

      <div className="flex gap-2">
        {['courses', 'compliance'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-md text-sm font-semibold capitalize transition ${tab === t ? 'bg-[#8BC53F] text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {loading ? <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div> : tab === 'courses' ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {courses.map(c => (
            <Card key={c.id} className="p-4 border-slate-200" data-testid={`admin-course-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px] font-bold">{c.category}</Badge>
                <button onClick={() => del(c.id)} className="text-slate-300 hover:text-red-500" data-testid={`delete-course-${c.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="font-bold text-slate-900 mt-2">{c.title}</div>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</p>
              <div className="flex items-center gap-3 mt-3 text-xs text-slate-500"><span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{c.ceHours} hr</span><span className="capitalize">{c.type}</span></div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr><th className="px-4 py-3 text-left">Technician</th><th className="px-4 py-3 text-left">CE Progress</th><th className="px-4 py-3 text-left">Renewal</th><th className="px-4 py-3 text-left">Status</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {compliance.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No technician accounts found yet.</td></tr>
              ) : compliance.map(row => <ComplianceRow key={row.email} row={row} onSaved={load} />)}
            </tbody>
          </table>
        </Card>
      )}

      {showAdd && <AddCourseModal onClose={() => setShowAdd(false)} onSaved={load} />}
    </div>
  );
}
